import { Phase, Process, Contract, Segment } from "@/types/contract";
import { TechnicalDataFormValues } from "./types";
import { tariffsForVTyearConsumption } from "@/constants/supplyPoint";
import { PlannedConsumptions } from "@/types/enumerations";
import { CircuitBreakers } from "@/types/enumerations";
import { isHousehold } from "@/utils/customer";
import { TechnicalDataGasFormValues } from "../TechnicalDataGas/form";
import { isLDSEanOrEic } from "@/utils/supplyPoint";
import { CustomerTypeValues } from "@/constants/customer";
import { TFunction } from "i18next";
import { EanPrefix } from "@/constants/common";

const isContractFromRequest = (contract: Contract) => contract.phase === Phase.CONTRACT_FROM_REQUEST;

const hasPreviousConnection = (contract: Contract) => {
  const supplyPoint = contract.supplyPoint;
  return supplyPoint.previousConnection === true || supplyPoint.sop?.self === true;
};

export const showTariff = (contract: Contract) => contract.process !== Process.PS && !isContractFromRequest(contract);
export const showTariffSelection = (contract: Contract) =>
  contract.process !== Process.ZDP || contract.supplyPoint.changeTariff === true;

export const showCircuitBreaker = (contract: Contract) => contract.process !== Process.PS && !isContractFromRequest(contract);
export const showCircuitBreakerSelection = (contract: Contract) =>
  contract.process !== Process.ZDP || contract.supplyPoint.changeCircuitBreaker === true;

export const showMeasurementType = (contract: Contract) =>
  contract.process !== Process.PS && !isContractFromRequest(contract) && contract.process !== Process.ZDP;

export const showConnectionAgreement = (contract: Contract) =>
  contract.process === Process.CHANGED_WITH_HAND_OVER && !isContractFromRequest(contract);

export const showCode = (contract: Contract) => {
  if (contract.process === Process.NEW_POINT) {
    return hasPreviousConnection(contract) && !isContractFromRequest(contract);
  }

  return contract.process !== Process.PS && contract.process !== Process.ZDP;
};

export const showCodeSecondary = (contract: Contract, formValues: TechnicalDataFormValues) =>
  contract.process === Process.NEW_POINT &&
  hasPreviousConnection(contract) &&
  !isContractFromRequest(contract) &&
  ["C46D", "C56D", "C27D"].includes(formValues.tariff ?? "");

export const showConnectionFee = (contract: Contract) =>
  contract.process === Process.NEW_POINT && contract.supplyPoint.sop?.self === false && !isContractFromRequest(contract);

export const showSupplyPointUsage = (contract: Contract) =>
  ((contract.process === Process.NEW_POINT || contract.process === Process.PS) && !isContractFromRequest(contract)) ||
  contract.process === Process.ZDP;

export const showDistributionArea = (contract: Contract) => {
  const supplyPoint = contract.supplyPoint;
  return (
    contract.process === Process.NEW_POINT &&
    supplyPoint.sop?.self === false &&
    supplyPoint.previousConnection === false &&
    !isContractFromRequest(contract)
  );
};

export const showConnectionType = (contract: Contract) =>
  contract.process === Process.NEW_POINT && !isContractFromRequest(contract);

export const showYearConsumption = (contract: Contract) =>
  contract.selfService === false ||
  contract.process === Process.NEW_POINT ||
  contract.process === Process.ZDP ||
  contract.process === Process.PS;

export const showYearConsumptionHigh = (contract: Contract, formValues: TechnicalDataFormValues) =>
  !showYearConsumption(contract) && tariffsForVTyearConsumption.includes(formValues.tariff ?? "");

export const showYearConsumptionHighLow = (contract: Contract, formValues: TechnicalDataFormValues) =>
  !showYearConsumption(contract) && !showYearConsumptionHigh(contract, formValues);

export const getEstimatedConsumption = (
  formValues: TechnicalDataFormValues,
  usageCoefficient = 1,
  plannedConsumptions: PlannedConsumptions[]
) => {
  let phase: number | null = null;
  let value: number | null = null;

  if (formValues.circuitBreaker === "self") {
    phase = formValues.circuitBreakerPhase ? parseInt(formValues.circuitBreakerPhase, 10) : null;
    value = formValues.circuitBreakerValue ?? null;
  } else if (formValues.circuitBreaker) {
    const formCircuitBreaker = getCircuitBreakerByIdentifier(formValues.circuitBreaker);

    phase = formCircuitBreaker?.circuitBreakerPhase ?? null;
    value = formCircuitBreaker?.circuitBreakerValue ?? null;
  }

  const consumption = plannedConsumptions.find(
    item =>
      item.name?.toLowerCase() === formValues.tariff?.toLowerCase() &&
      item.circuitBreakerPhase === phase &&
      (item.circuitBreakerValueMin ?? 0) <= (value ?? 0) &&
      (item.circuitBreakerValueMax ?? 0) >= (value ?? 0)
  );

  return consumption
    ? { min: (consumption.minMwh ?? 0) * usageCoefficient, max: (consumption.maxMwh ?? 0) * usageCoefficient }
    : undefined;
};

export const showReservedPowerFee = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const formCircuitBreaker = getCircuitBreakerByIdentifier(formValues.circuitBreaker ?? "");

  if (
    !formCircuitBreaker ||
    !contract.supplyPoint.currentCircuitBreakerPhase ||
    !contract.supplyPoint.currentCircuitBreakerValue
  ) {
    return false;
  }

  return (
    contract.process === Process.ZDP &&
    contract.supplyPoint.sop?.self === false &&
    (formCircuitBreaker.circuitBreakerPhase !== contract.supplyPoint.currentCircuitBreakerPhase ||
      formCircuitBreaker.circuitBreakerValue !== contract.supplyPoint.currentCircuitBreakerValue)
  );
};

export const showDoubleMeasurement = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const supplyPoint = contract.supplyPoint;
  return (
    contract.process === Process.ZDP &&
    !isHousehold(contract.customer) &&
    formValues.tariff &&
    supplyPoint.currentTariff !== formValues.tariff &&
    (["C27D", "C46D"].includes(formValues.tariff) || (formValues.tariff === "C56D" && formValues.supplyPointUsage !== "01"))
  );
};

export const showCodeSecondaryZDP = (contract: Contract, formValues: TechnicalDataFormValues) => {
  const is1TTariff = ["C01D", "C02D", "C03D", "D01D", "D02D", "C62D"];
  const supplyPoint = contract.supplyPoint;
  return (
    contract.process === Process.ZDP &&
    supplyPoint.currentTariff !== formValues.tariff &&
    formValues.doubleMeasurement === "true" &&
    formValues.tariff &&
    !is1TTariff.includes(formValues.tariff) &&
    !isHousehold(contract.customer)
  );
};

export const getCircuitBreakerIdentifier = (circuitBreaker: CircuitBreakers) => {
  return `${circuitBreaker.circuitBreakerPhase}x${circuitBreaker.circuitBreakerValue}`;
};

export const getCircuitBreakerByIdentifier = (identifier: string): Partial<CircuitBreakers> | null => {
  const [phaseStr, valueStr] = identifier.split("x");
  const phase = parseInt(phaseStr ?? "", 10);
  const value = parseInt(valueStr ?? "", 10);
  if (isNaN(phase) || isNaN(value)) {
    return null;
  }
  return { circuitBreakerPhase: phase, circuitBreakerValue: value };
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const tariffCheckForRevisionReport = (currentTariff: string | null, tariff: string | null) => {
  if (!currentTariff || !tariff) {
    return null;
  }
  if (
    ["C01D", "C02D", "C03D", "C35D", "C62D"].includes(currentTariff) &&
    ["C25D", "C26D", "C27D", "C45D", "C46D", "C56D"].includes(tariff)
  ) {
    return "K";
  }
  if (["C55D", "C56D"].includes(currentTariff) && ["C25D", "C26D", "C27D", "C45D", "C46D"].includes(tariff)) {
    return "K";
  }
  if (["C27D", "C45D"].includes(currentTariff) && ["C25D", "C26D", "C45D", "C46D", "C56D"].includes(tariff)) {
    return "K";
  }
  if (currentTariff === "C25D" && ["C26D", "C27D", "C45D", "C46D", "C56D", "C62D"].includes(tariff)) {
    return "K";
  }
  if (currentTariff === "C26D" && ["C27D", "C45D", "C46D", "C56D", "C62D"].includes(tariff)) {
    return "K";
  }
  if (currentTariff === "C46D" && ["C25D", "C26D", "C27D", "C56D"].includes(tariff)) {
    return "K";
  }
  if (["D01D", "D02D"].includes(currentTariff) && ["D25D", "D26D", "D57D"].includes(tariff)) {
    return "K";
  }
  if (["D27D", "D61D", "D35D"].includes(currentTariff) && ["D25D", "D26D", "D57D"].includes(tariff)) {
    return "K";
  }
  if (["D45D", "D56D"].includes(currentTariff) && ["D25D", "D26D"].includes(tariff)) {
    return "K";
  }
  if (currentTariff === "D26D" && tariff === "D57D") {
    return "K";
  }
  if (currentTariff === "D25D" && ["D26D", "D57D"].includes(tariff)) {
    return "K";
  }
  if (currentTariff === "D57D" && ["D25D", "D26D"].includes(tariff)) {
    return "K";
  }
  return null;
};

const getRevisionReportForZDP = (contract: Contract, values: TechnicalDataFormValues) => {
  const supplyPoint = contract.supplyPoint;
  const distributorId = supplyPoint.distribution.distributorId?.toString() ?? "";

  if (["ED-CEZ", "ED-PRE"].includes(distributorId) || isLDSEanOrEic(contract, values.code)) {
    return "B";
  }
  if (distributorId === "ED-EON") {
    return gerRevisionReportForEGD(contract, values);
  }
  return null;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const gerRevisionReportForEGD = (contract: Contract, values: TechnicalDataFormValues) => {
  const supplyPoint = contract.supplyPoint;
  const formCircuitBreaker = getCircuitBreakerByIdentifier(values.circuitBreaker ?? "");
  const currentTariff = supplyPoint.currentTariff;
  const tariff = values.tariff ?? "";

  if (
    formCircuitBreaker &&
    supplyPoint.currentCircuitBreakerPhase === formCircuitBreaker.circuitBreakerPhase &&
    supplyPoint.currentCircuitBreakerValue === formCircuitBreaker.circuitBreakerValue &&
    currentTariff !== tariff
  ) {
    if (!currentTariff || !tariff) {
      return null;
    }
    if (["D01D", "D02D"].includes(currentTariff) && ["D27D", "D61D"].includes(tariff)) {
      return "B";
    }
    if (currentTariff === "D57D" && tariff === "D27D") {
      return "B";
    }
  }
  if (
    formCircuitBreaker &&
    (supplyPoint.currentCircuitBreakerPhase !== formCircuitBreaker.circuitBreakerPhase ||
      supplyPoint.currentCircuitBreakerValue !== formCircuitBreaker.circuitBreakerValue)
  ) {
    return "K";
  }
  if (supplyPoint.currentTariff !== values.tariff) {
    return tariffCheckForRevisionReport(supplyPoint.currentTariff, values.tariff);
  }
  return null;
};

export const getRevisionReport = (contract: Contract, values: TechnicalDataFormValues) => {
  if (contract.process === Process.NEW_POINT) {
    return "K";
  }
  if (contract.process === Process.ZDP) {
    return getRevisionReportForZDP(contract, values);
  }
  return null;
};

export const getDepositFrequency = (contract: Contract, values: TechnicalDataFormValues | TechnicalDataGasFormValues) => {
  const currentSupplyPoint = contract.supplyPoint;
  if (contract.customer.specificDepositType === "00") {
    return "00";
  }

  if (
    contract.selfService === true &&
    (values.measurementType === "B" || isLDSEanOrEic(contract, values.code)) &&
    [
      Process.SUPPLIER_CHANGE,
      Process.DPI,
      Process.CHANGED_WITH_HAND_OVER,
      Process.UNAUTHORIZED_SP,
      Process.NEW_POINT,
      Process.UNAUTHORIZED,
      Process.UNAUTHORIZED_PREVENTION,
      Process.PS,
    ].includes(contract.process)
  ) {
    return "00";
  }
  if (
    [Process.PS, Process.NEW_POINT].includes(contract.process) &&
    !isLDSEanOrEic(contract, values.code) &&
    values.measurementType !== "B" &&
    contract.selfService === true
  ) {
    return "01";
  }
  if (
    (contract.process === Process.ZDP &&
      currentSupplyPoint.measurementType === "C" &&
      !isLDSEanOrEic(contract, values.code) &&
      [4, null].includes(currentSupplyPoint.measurementCategory)) ||
    (currentSupplyPoint.depositFrequency === "00" && contract.segment === Segment.B2B)
  ) {
    return "01";
  }

  return currentSupplyPoint.depositFrequency;
};

export const isConsumptionEstimated = (contract: Contract) => {
  return contract.process === Process.NEW_POINT || contract.process === Process.PS;
};

export const getRsNote = (contract: Contract, values: TechnicalDataFormValues, t: TFunction<"supplyPoint">) => {
  if (showD27DNote(contract, values)) {
    return t("technicalData.evInfoConfirmation");
  }
  if (
    contract.process === Process.NEW_POINT &&
    contract.segment === Segment.B2B &&
    contract.selfService === true &&
    values.codeSecondary
  ) {
    return `${EanPrefix}${values.codeSecondary}`;
  }
  return null;
};

export const showD27DNote = (contract: Contract, values: TechnicalDataFormValues) => {
  return (
    contract.process === Process.ZDP &&
    values.tariff === "D27D" &&
    contract.customer.customerType === CustomerTypeValues.Tradesman
  );
};
